import { AfterViewInit, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Select, Store } from '@ngxs/store';
import { Observable } from 'rxjs/internal/Observable';
import { getFileTypes } from 'src/app/inventory/interfaces/file-types-list';
import { ICollectOrderDetailsFormValue } from 'src/app/inventory/interfaces/ICollectOrderDetailsFormValue';
import { IUpdateOrderRequest } from 'src/app/inventory/interfaces/IUpdateOrderRequest';
import { IFileUploadObject } from 'src/app/shared/components/file-uploader/file-upload-object.interface';
import { IOptionItem } from 'src/app/shared/interfaces/option-item.interface';
import { OrderTypeEnum } from 'src/app/shared/interfaces/OrderTypeEnum';
import { ISaveAttachedFileEvent } from 'src/app/shared/interfaces/save-attached-file-event.interface';
import { NavigatedFromPipe } from 'src/app/shared/pipes/navigated-from/navigated-from.pipe';
import { PharmacyState } from 'src/app/shared/states/pharmacy/pharmacy.state';
import { BaseTableDirective } from '../../../../shared/directives/base-table.directive';
import { getDateFromISO, getUTC } from '../../../../shared/helpers/helpers';
import { IEdiOrder } from '../../../../shared/interfaces/EDI-order.interface';
import {
  GetInventoryOrderDetails,
  SetInventoryOrderDetails,
  UpdateOrder,
} from '../../../../shared/states/orders/orders.actions';
import { OrdersState } from '../../../../shared/states/orders/orders.state';
import { OrdersService } from '../../receive-edi/orders.service';
import { FileService } from 'src/app/shared/services/file-service';
import { PdfSignatureService } from 'src/app/shared/services/pdf-signature-service';
import { FormControl, FormGroup } from '@angular/forms';
import { CSOSRequiredValidator } from 'src/app/shared/validators/CSOS.validator';

@UntilDestroy()
@Component({
  selector: 'app-order-details-table',
  templateUrl: './order-details-table.component.html',
  styleUrls: [
    './order-details-table.component.scss',
    '../../../inventory.scss',
  ],
})
export class OrderDetailsTableComponent
  extends BaseTableDirective
  implements OnInit, OnDestroy
{
  @Select(OrdersState.orderDetails) orderDetails$: Observable<IEdiOrder>;
  @Select(OrdersState.orderFiles) orderFiles$: Observable<IFileUploadObject[]>;
  @Select(PharmacyState.inventoryLocationsEnabled) inventoryLocationsEnabled$: Observable<boolean>;
  fileTypes: IOptionItem[] = getFileTypes(OrderTypeEnum.Purchase);
  
  getDateFromISO = getDateFromISO;

  tableColumns = {
    friendlyName: true,
  };

  navigation: any;
  navigationState: any;
  orderDetails: IEdiOrder;

  isEditMode: boolean;

  orderDetailsFiles: IFileUploadObject[] = [];
  updatedAttachments: IFileUploadObject[] = [];

  pinDialog: { show: boolean; type: 'saveAttachment' | 'saveUpdatedOrder' } = {show: false, type: 'saveAttachment'};
  showResetPinDialog: boolean;
  focusPin: boolean;

  signature: string;
  form: FormGroup;

  displayEditOrderDetailsDialog: boolean;

  collectedOrderDetails: ICollectOrderDetailsFormValue;

  @ViewChild('fileAttachment') fileAttachment;
  fileAttachmentHeight: number;

  @ViewChild('buyerSupplier') buyerSupplier;
  buyerSupplierHeight: number;

  constructor(
    protected store: Store,
    private router: Router,
    private orderService: OrdersService,
    private navigatedFromPipe: NavigatedFromPipe,
    private fileService: FileService,
    private route: ActivatedRoute,
    private pdfSignatureService: PdfSignatureService,
  ) {
    super(store);
    const id = this.route.snapshot.params?.id;
    if (id) {
      this.store.dispatch(new GetInventoryOrderDetails(id));
    }
    this.navigationState = this.router.getCurrentNavigation()?.extras?.state;
    this.form = new FormGroup({
      csos: new FormControl()
    })
  }

  ngOnInit(): void {
    this.orderDetails$
      .pipe(untilDestroyed(this))
      .subscribe((data: IEdiOrder) => {
        const shipDateUtc = getUTC(data?.shippedDate);
        this.orderDetails = { ...data, shippedDate: shipDateUtc };
        this.collectedOrderDetails = {
          invoiceNumber: data?.invoiceNumber,
          poNumber: data?.purchaseOrder,
          shipDate: shipDateUtc,
         // csos: data?.csos ?? this.form.get('csos')?.value,
         csos: data?.csos,
          supplierDetails: data?.supplierDetails,
          buyerDetails: data?.buyerDetails
        };
        const csos = this.form.get('csos');
        csos?.patchValue(data?.csos);
        csos.addValidators(CSOSRequiredValidator.bind(this, this.orderDetails?.orderLineItems));
      });

    this.orderFiles$
      .pipe(untilDestroyed(this))
      .subscribe((files: IFileUploadObject[]) => {
        this.orderDetailsFiles = files;
        setTimeout(() => {
          this.fileAttachmentHeight = this.fileAttachment.nativeElement.offsetHeight;
          this.buyerSupplierHeight = this.buyerSupplier?.nativeElement?.offsetHeight || 0;
        });
      });

      this.form.get('csos').valueChanges
      .pipe(untilDestroyed(this))
      .subscribe(val => this.collectedOrderDetails.csos = val);
  }

  saveCollectedOrderDetails(
    collectedOrderDetails: ICollectOrderDetailsFormValue
  ): void {
    this.collectedOrderDetails = collectedOrderDetails;
    this.form.get('csos').patchValue(this.collectedOrderDetails.csos);
    this.displayEditOrderDetailsDialog = false;

    this.pinDialog.show = true;
    this.pinDialog.type = 'saveUpdatedOrder';
  }

  sortByColumn(columnName: string): void {
    const orderedTableData = this.sort(
      this.orderDetails.orderLineItems,
      columnName
    );
    this.store.dispatch(
      new SetInventoryOrderDetails({
        ...this.orderDetails,
        orderLineItems: orderedTableData,
      })
    );
  }

  saveAttachmentsList(event: ISaveAttachedFileEvent): void {
    if (event.attachments) {
      this.pinDialog.type = 'saveAttachment';
      this.pinDialog.show = true;
      this.isEditMode = event.isEditMode;
      setTimeout(() => (this.focusPin = true));
      this.updatedAttachments = event.attachments;
      this.signature = event.signature;
    }
  }

  downloadFile(item: IFileUploadObject): void {
    this.fileService.downloadFile(item);
  }

  closePinDialog(): void {
    this.pinDialog.show = false;
  }

  openResetPinDialog(): void {
    this.focusPin = false;
    this.pinDialog.show = false;
    this.showResetPinDialog = true;
  }

  closeResetPinDialog(): void {
    this.showResetPinDialog = false;
    this.pinDialog.show = true;
    this.focusPin = true;
  }

  afterPinUpdated(success: boolean): void {
    // TODO: show message of pin update
    if (success) {
      this.closeResetPinDialog();
    }
  }

  async validateRequest({ success, pin, response }): Promise<void> {
    if (success) {
      if ((this.pinDialog.type === 'saveAttachment')) {
        await this.saveAttachment({pin, response})
        .then(() => this.orderDetails.csos = this.form.get('csos')?.value);
      } else {
        
        this.orderDetails.pin = pin;
        this.orderDetails.supplierDetails = this.collectedOrderDetails?.supplierDetails;
        this.orderDetails.buyerDetails = this.collectedOrderDetails?.buyerDetails;
        this.orderDetails.supplierDetailsID = this.collectedOrderDetails?.supplierDetails?.id;
        this.orderDetails.buyerDetailsID = this.collectedOrderDetails?.buyerDetails?.id;
        this.orderDetails.invoiceNumber = this.collectedOrderDetails?.invoiceNumber;
        this.orderDetails.purchaseOrder = this.collectedOrderDetails?.poNumber;
        this.orderDetails.shippedDate = this.collectedOrderDetails?.shipDate;
        this.orderDetails.csos = this.collectedOrderDetails?.csos;
        const updateRequest = {...this.orderDetails};
        delete updateRequest.buyerDetails;
        delete updateRequest.supplierDetails;
        this.store.dispatch(new UpdateOrder(updateRequest as IUpdateOrderRequest));
      }
      this.pinDialog.show = false;
    }
  }

  private async saveAttachment({ pin, response}) {
    //Attach signature before uploading a file
    if (this.signature) {
        const name = response?.user?.firstName + ' ' + response?.user?.lastName;
        await this.pdfSignatureService.sign(
        this.updatedAttachments, 
        this.signature, 
        name, 
        this.store.selectSnapshot(PharmacyState.currentPharmacy)?.name,
        new Date(this.orderDetails?.receivedDate),
        new Date(this.orderDetails?.processedDate || this.orderDetails?.createDate)
      );
    }

    this.fileService.saveAttachmentsList(
      { orderId: this.orderDetails?.id, pin, csos: this.form.get('csos')?.value },
      this.isEditMode,
      this.orderDetailsFiles,
      this.updatedAttachments
    );
  }

  navigateBack() {
    const navigatedFrom = this.navigatedFromPipe.transform(this.navigationState, 'Purchase Orders', '/inventory/order-history')?.link;
    this.router.navigate([navigatedFrom], {state: {
      isNavigatedBack: true
    }});
  }

  calculateMaxHeight(): string {
    return `calc(100vh - 227px - ${this.fileAttachmentHeight + this.buyerSupplierHeight}px)`;
  }

  ngOnDestroy(): void {
    this.updatedAttachments = null;
    this.signature = null;
  }
}
